import { ofType } from "redux-observable";
import { filter, map } from "rxjs/operators";
import { createSelector } from "reselect";

function consolidate(purchaseOrders, skip, skipItems) {
  return purchaseOrders.reduce(
    (prev, p, i, a) => {
      const laterOrders = a.slice(i + 1, a.length - skip);

      const items = groupItems([...p.items, ...prev.remainingItems]);
      const itemSplit = items.reduce(
        ({ carriedItems, deliveryItems }, item) => {
          const packagingAlternates = item.item.packagingAlternates;
          if (
            packagingAlternates.length === 0 ||
            skipItems.some((s) => s === item.id)
          ) {
            // Item has no packaging alternates or is skipped. Include it on this delivery
            deliveryItems.push(item);
          } else {
            // Assume first alternate match for now
            const firstAlternate = packagingAlternates.find(
              (a) => item.quantity >= a.yield
            );
            let remainingQuantity = item.quantity;
            if (firstAlternate) {
              const deliveryQuantity = Math.floor(
                item.quantity / firstAlternate.yield
              );
              deliveryItems.push({
                ...item,
                consolidated: true,
                item: firstAlternate.item,
                quantity: deliveryQuantity,
              });
              remainingQuantity =
                Math.round(100000 * (item.quantity % firstAlternate.yield)) /
                100000;
            }

            // Make sure there is enough quantity on future orders to hit the alternate
            const futureQuantity = laterOrders.reduce((q, o) => {
              return o.items
                .filter((i) => i.item.id === item.item.id)
                .filter((i) => !skipItems.some((s) => s === i.id))
                .reduce((q2, i) => q2 + i.quantity, q);
            }, remainingQuantity);

            // Assume first alternate match for now
            const alternate = packagingAlternates.find(
              (a) => futureQuantity >= a.yield
            );
            if (!alternate) {
              // Add the remaining quantity to this delivery
              deliveryItems.push({
                ...item,
                quantity: remainingQuantity,
              });
            } else {
              // Push the remaining quantity to a later delivery for consolidation
              carriedItems.push({
                ...item,
                quantity: remainingQuantity,
              });
            }
          }

          return {
            carriedItems,
            deliveryItems,
          };
        },
        {
          carriedItems: [],
          deliveryItems: [],
        }
      );

      prev.purchaseOrders.push({
        ...p,
        items: itemSplit.deliveryItems,
      });
      prev.remainingItems = itemSplit.carriedItems;
      return prev;
    },
    { purchaseOrders: [], remainingItems: [] }
  ).purchaseOrders;
}

function groupItems(items) {
  const byId = items.reduce((byId, i) => {
    byId[i.item.id] = byId[i.item.id] || {
      ...i,
      quantity: 0,
    };
    byId[i.item.id].quantity += i.quantity;
    return byId;
  }, {});
  return Object.values(byId);
}

function sortPurchaseOrders(a, b) {
  return a.deliveryOrder - b.deliveryOrder || a.id - b.id;
}

function sortItems(a, b) {
  return a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
}

// Action types
const TOGGLE_LOAD_REPORT_MODAL = "TOGGLE_LOAD_REPORT_MODAL";
const LOAD_REPORT_COMPLETE = "LOAD_REPORT_COMPLETE";
const SKIP_ITEM = "SKIP_ITEM";

const DEFAULT_STATE = {
  loadReport: [],
  skipConsolidatingStops: 1,
  skipItems: [],
  show: false,
};

// Reducer
export default function (state = DEFAULT_STATE, { payload, type }) {
  switch (type) {
    case LOAD_REPORT_COMPLETE:
      return {
        ...state,
        loadReport: payload,
      };
    case SKIP_ITEM:
      return {
        ...state,
        skipItems: [...state.skipItems, payload],
      };
    case TOGGLE_LOAD_REPORT_MODAL:
      if (state.show) {
        return DEFAULT_STATE;
      } else {
        return {
          ...state,
          show: true,
        };
      }
    default:
      return state;
  }
}

// Action creators
const loadReportComplete = (payload) => ({
  type: LOAD_REPORT_COMPLETE,
  payload,
});

export const skipItem = (id) => ({
  type: SKIP_ITEM,
  payload: id,
});

export const toggleLoadReportModal = () => ({
  type: TOGGLE_LOAD_REPORT_MODAL,
});

// Epics
export const epic = (action$, state$) =>
  action$.pipe(
    ofType(TOGGLE_LOAD_REPORT_MODAL, SKIP_ITEM),
    filter(() => state$.value.modals.loadReportModal.show),
    map(() => loadReport(state$.value)),
    map((equipment) => {
      const { skipConsolidatingStops, skipItems } =
        state$.value.modals.loadReportModal;
      return equipment.map((e) => ({
        ...e,
        consolidatedLoad: consolidate(
          e.purchaseOrders,
          skipConsolidatingStops,
          skipItems
        ),
      }));
    }),
    map(loadReportComplete)
  );

// Selectors
const loadReport = createSelector(
  (state) => state.events.events,
  (events) => {
    const byEquipment = events
      .filter((e) => e.purchaseOrder || e.inventoryPurchaseOrder)
      .filter((e) => {
        const po = e.purchaseOrder || e.inventoryPurchaseOrder;
        return po.items && po.items.some((i) => !!i.item && i.quantity !== 0);
      })
      .reduce((l, e) => {
        const equipment = l[(e.crew && e.crew.equipment.id) || 0] || {
          equipment: (e.crew && e.crew.equipment) || {
            id: 0,
            name: "None",
          },
          purchaseOrders: [],
        };

        // Get the purchase order (regular or inventory)
        const purchaseOrder = e.purchaseOrder || e.inventoryPurchaseOrder;
        const normalizedPO = {
          ...purchaseOrder,
          isInventoryPO: !!e.inventoryPurchaseOrder,
          items: purchaseOrder.items
            .filter((i) => !!i.item && i.quantity !== 0)
            .sort(sortItems),
        };

        return {
          ...l,
          [(e.crew && e.crew.equipment.id) || 0]: {
            ...equipment,
            purchaseOrders: [...equipment.purchaseOrders, normalizedPO],
          },
        };
      }, {});

    return Object.values(byEquipment).map((e) => ({
      ...e,
      purchaseOrders: e.purchaseOrders.sort(sortPurchaseOrders).reverse(),
    }));
  }
);

export const loadReportModalSelector = (state) => ({
  ...state.modals.loadReportModal,
  showConsolidatedLoad: state.modals.loadReportModal.loadReport.some((e) =>
    e.purchaseOrders.some((p) =>
      p.items.some(
        (i) =>
          i.item.packagingAlternates && i.item.packagingAlternates.length > 0
      )
    )
  ),
});
